import { useState, useEffect } from "react";
import { Treemap, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Search, X } from "lucide-react";

const PALETTE = [
  "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#A855F7",
  "#06B6D4", "#EF4444", "#84CC16", "#94A3B8", "#0EA5E9",
  "#FB7185", "#22D3EE", "#F97316", "#34D399", "#C084FC",
  "#FCD34D", "#60A5FA", "#FB923C", "#4ADE80", "#E879F9",
];

function fmtMoney(n) {
  if (!n && n !== 0) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Custom content renderer for treemap cells.
const CellContent = (props) => {
  const { x, y, width, height, name, cost, fill, onSelect } = props;
  const tooSmall = width < 60 || height < 28;
  return (
    <g style={{ cursor: "pointer" }} onClick={() => onSelect && onSelect(name)}>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#0A0B10" strokeWidth={2} />
      {!tooSmall && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle"
                fill="#0A0B10" fontWeight="700" fontSize={width < 100 ? 9 : 12}
                style={{ pointerEvents: "none" }}>
            {name}
          </text>
          <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle"
                fill="#0A0B10" fontSize={width < 100 ? 9 : 11}
                style={{ pointerEvents: "none" }}>
            {fmtMoney(cost)}
          </text>
        </>
      )}
    </g>
  );
};

export default function MaterialTreemap({ data, showMexico = false }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const sorted = (data || []).filter((d) => d.cost > 0).sort((a, b) => b.cost - a.cost);
  const tree = sorted.map((d, i) => ({
    name: d.category,
    cost: d.cost,
    size: d.cost,
    fill: PALETTE[i % PALETTE.length],
  }));

  const openCategory = async (name) => {
    setCategory(name);
    setOpen(true);
    setLoading(true);
    setSearch("");
    try {
      const { data: rows } = await api.get("/overview/material-category-items", {
        params: { category: name, show_mexico: showMexico },
      });
      setItems(rows);
    } finally { setLoading(false); }
  };

  // attach onSelect into the content function via closure
  const Content = (props) => <CellContent {...props} onSelect={openCategory} />;

  const filtered = items.filter((i) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (i.item || "").toLowerCase().includes(q) ||
           (i.job || "").toLowerCase().includes(q);
  });
  const totalCost = filtered.reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <>
      <div className="bg-[#12141D] border border-border p-5">
        <h3 className="font-heading font-semibold">Material Cost Treemap</h3>
        <p className="text-xs text-muted-foreground mb-4">Category breakdown by cost — click any tile to drill into items</p>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <Treemap
              data={tree}
              dataKey="size"
              stroke="#0A0B10"
              content={<Content />}
              isAnimationActive={false}
            />
          </ResponsiveContainer>
        </div>

        {/* compact legend grid below */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-5">
          {sorted.map((d, i) => (
            <button
              key={d.category}
              data-testid={`treemap-legend-${d.category.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => openCategory(d.category)}
              className="flex items-center justify-between border border-border bg-[#0A0B10] px-3 py-2 text-left hover:border-[#00D4FF] transition-colors"
            >
              <span className="flex items-center gap-2 text-xs">
                <span className="h-2 w-2" style={{ background: PALETTE[i % PALETTE.length] }} />
                {d.category}
              </span>
              <span className="text-xs font-mono">{fmtMoney(d.cost)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Drill-down modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="material-items-modal"
          className="bg-[#12141D] border-[#00D4FF]/30 max-w-4xl rounded-none p-0 text-white"
        >
          <DialogHeader className="p-5 border-b border-border">
            <DialogTitle className="font-heading text-lg tracking-tight flex items-center justify-between gap-4">
              <span>{category}</span>
              <span className="text-xs font-mono font-normal text-muted-foreground">
                {filtered.length} items · {fmtMoney(totalCost)}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="p-5 border-b border-border">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="material-items-search"
                placeholder="Search by item number or job…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-[#0A0B10] border-border text-white rounded-none focus-visible:ring-[#00D4FF]"
              />
            </div>
          </div>

          <div className="max-h-[55vh] overflow-auto">
            <table className="w-full text-left border-collapse" data-testid="material-items-table">
              <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28] sticky top-0">
                <tr>
                  <th className="py-3 px-4">Item Number</th>
                  <th className="py-3 px-4">Job</th>
                  <th className="py-3 px-4 text-right">Units</th>
                  <th className="py-3 px-4 text-right">Cost</th>
                  <th className="py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="py-8 text-center text-muted-foreground text-xs tracking-widest uppercase">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-muted-foreground text-xs tracking-widest uppercase">No items</td></tr>
                )}
                {!loading && filtered.map((i, idx) => (
                  <tr key={idx} className="border-b border-border hover:bg-[#1A1C28]">
                    <td className="py-3 px-4 text-sm font-mono">{i.item || "—"}</td>
                    <td className="py-3 px-4 text-sm font-mono text-[#00D4FF]">{i.job || "—"}</td>
                    <td className="py-3 px-4 text-sm font-mono text-right">{i.units?.toLocaleString() ?? "—"}</td>
                    <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(i.cost)}</td>
                    <td className="py-3 px-4 text-sm font-mono">{i.date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
